/**
 * 实时语音识别服务
 * 使用后端代理服务器进行语音识别
 * 参考实现：asr-realtime.html
 */

// 配置项 - 建议从环境变量或配置文件中读取
const FUNASR_CONFIG = {
  // WebSocket 代理服务器地址（后端代理服务器）
   WS_URL: 'wss://www.test.hehuitech.cn/websocket/websockets/ws/speech/gummy',//长语音识别
  //WS_URL: 'ws://172.16.16.103:9010/ws/speech/gummy',//长语音识别
  // 采样率：8000 或 16000
  SAMPLE_RATE: 16000,
  // 音频格式：pcm
  FORMAT: 'pcm',
  // 音频声道数
  CHANNELS: 1,
  // 每次发送的音频数据大小（样本数）
  // 注意：createScriptProcessor 的 bufferSize 必须是 2 的幂次方（256-16384）
  // 使用 4096 样本 ≈ 256ms 的音频数据（16000Hz 采样率）
  BUFFER_SIZE: 4096, // bufferSize (样本数)，必须是 2 的幂次方
}

/**
 * 语音识别服务类
 */
class SpeechRecognitionService {
  constructor(config = {}) {
    this.config = { ...FUNASR_CONFIG, ...config }
    this.ws = null
    this.audioContext = null
    this.mediaStream = null
    this.audioProcessor = null
    this.isRecording = false
    this.isConnected = false
    
    // 回调函数
    this.onResult = null // 识别结果回调 (text: string, isSentenceEnd: boolean) => void
    this.onError = null // 错误回调 (error: Error) => void
    this.onStatusChange = null // 状态变化回调 (status: string) => void
  }

  /**
   * 建立 WebSocket 连接
   * 连接到后端代理服务器，认证由后端处理
   */
  async connect() {
    if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      return
    }

    try {
      // 构建WebSocket URL，包含format和sampleRate参数
      const wsUrl = `${this.config.WS_URL}?format=${this.config.FORMAT}&sampleRate=${this.config.SAMPLE_RATE}`
      
      // 创建WebSocket连接（连接到后端代理服务器）
      this.ws = new WebSocket(wsUrl)

      // 连接打开
      this.ws.onopen = () => {
        this.isConnected = true
        this.onStatusChange && this.onStatusChange('connected')
      }

      // 接收消息
      this.ws.onmessage = (event) => {
        this.handleMessage(event)
      }

      // 连接错误
      this.ws.onerror = (error) => {
        this.isConnected = false
        this.onStatusChange && this.onStatusChange('error')
        
        let errorMsg = 'WebSocket 连接错误'
        if (this.ws?.readyState === WebSocket.CLOSED) {
          errorMsg = 'WebSocket 连接失败，请检查：\n1. 网络连接是否正常\n2. 后端代理服务器是否正常运行\n3. 服务地址是否正确'
        }
        this.onError && this.onError(new Error(errorMsg))
      }

      // 连接关闭
      this.ws.onclose = (event) => {
        this.isConnected = false
        this.onStatusChange && this.onStatusChange('closed')
        
        // 如果不是正常关闭，触发错误回调
        if (event.code !== 1000 && this.isRecording) {
          let errorMsg = 'WebSocket 连接已断开'
          
          switch (event.code) {
            case 1006:
              errorMsg = 'WebSocket 连接异常断开\n\n可能的原因：\n1. 网络连接不稳定\n2. 后端代理服务器异常\n\n请检查后端代理服务器状态。'
              break
            case 1008:
              errorMsg = 'WebSocket 连接被拒绝\n\n可能的原因：\n1. 权限不足\n2. 认证失败\n\n请检查服务配置。'
              break
            default:
              if (event.reason) {
                errorMsg = `WebSocket 连接失败\n\n错误代码: ${event.code}\n错误原因: ${event.reason}`
              } else {
                errorMsg = `WebSocket 连接失败\n\n错误代码: ${event.code}`
              }
          }
          
          this.onError && this.onError(new Error(errorMsg))
        }
      }
    } catch (error) {
      this.onError && this.onError(error)
      throw error
    }
  }

  /**
   * 处理WebSocket消息
   * 消息格式：
   * - { type: 'connected', message: ... } - 连接成功
   * - { type: 'recognition_result', result: '...', taskId: '...' } - 识别结果
   * - { type: 'result', text: '...', isSentenceEnd: ... } - 识别结果（兼容格式）
   * - { type: 'error', message: '...' } - 错误
   * - { type: 'complete', ... } - 完成
   */
  handleMessage(event) {
    try {
      // 处理文本消息（JSON格式）
      const message = JSON.parse(event.data)

      switch (message.type) {
        case 'connected':
          // 连接成功
          this.onStatusChange && this.onStatusChange('connected')
          break

        case 'result':
        case 'recognition_result':
          // 识别结果
          // 支持两种格式：{ type: 'result', text: ... } 或 { type: 'recognition_result', result: ... }
          const resultText = message.text || message.result
          if (resultText) {
            this.onResult && this.onResult(resultText, message.isSentenceEnd || false)
          }
          break

        case 'error':
          // 识别错误
          const errorMsg = message.message || message.error || '识别错误'
          this.onError && this.onError(new Error(errorMsg))
          break

        case 'complete':
          // 识别完成
          this.onStatusChange && this.onStatusChange('completed')
          break

        default:
          // 未知消息类型，忽略
          break
      }
    } catch (error) {
      // 如果解析失败，可能是二进制数据或其他格式，忽略
      this.onError && this.onError(error)
    }
  }

  /**
   * 开始录音和识别
   */
  async start() {
    if (this.isRecording) {
      return
    }

    try {
      // 先建立WebSocket连接
      await this.connect()

      // 请求麦克风权限
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.config.SAMPLE_RATE,
          channelCount: this.config.CHANNELS,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })

      // 创建音频上下文
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: this.config.SAMPLE_RATE
      })

      // 创建音频源
      const source = this.audioContext.createMediaStreamSource(this.mediaStream)

      // 创建音频处理节点
      // 注意：ScriptProcessorNode 已废弃，但为了兼容性仍使用
      this.audioProcessor = this.audioContext.createScriptProcessor(
        this.config.BUFFER_SIZE,
        this.config.CHANNELS,
        this.config.CHANNELS
      )

      // 处理音频数据
      this.audioProcessor.onaudioprocess = (event) => {
        // 只有在连接且正在录音时才发送音频数据
        if (!this.isRecording || !this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
          return
        }

        // 获取音频数据
        const inputData = event.inputBuffer.getChannelData(0)
        
        // 转换为PCM格式
        const pcmData = this.convertFloat32ToInt16(inputData)
        
        // 发送音频数据
        this.sendAudioData(pcmData)
      }

      // 连接音频处理节点
      source.connect(this.audioProcessor)
      this.audioProcessor.connect(this.audioContext.destination)

      this.isRecording = true
      this.onStatusChange && this.onStatusChange('recording')
    } catch (error) {
      this.onError && this.onError(error)
      throw error
    }
  }

  /**
   * 停止录音和识别
   */
  async stop() {
    if (!this.isRecording) {
      return
    }

    this.isRecording = false

    // 发送停止命令
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send('stop')
      } catch (error) {
        // 发送失败，继续执行清理操作
      }
    }

    // 停止音频处理
    if (this.audioProcessor) {
      this.audioProcessor.disconnect()
      this.audioProcessor = null
    }

    // 关闭音频上下文
    if (this.audioContext) {
      await this.audioContext.close()
      this.audioContext = null
    }

    // 停止媒体流
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop())
      this.mediaStream = null
    }

    // 关闭WebSocket连接
    if (this.ws) {
      this.ws.close(1000, '正常关闭')
      this.ws = null
    }

    this.onStatusChange && this.onStatusChange('stopped')
  }

  /**
   * 发送音频数据
   */
  sendAudioData(pcmData) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    // 发送二进制音频数据（PCM格式）
    this.ws.send(pcmData)
  }

  /**
   * 将 Float32Array 转换为 Int16 PCM 格式
   * 参考：asr-realtime.html 中的实现
   */
  convertFloat32ToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length)
    for (let i = 0; i < float32Array.length; i++) {
      // 限制范围并转换为16位整数
      const s = Math.max(-1, Math.min(1, float32Array[i]))
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
    }
    return int16Array.buffer
  }

  /**
   * 设置回调函数
   */
  setCallbacks(callbacks) {
    if (callbacks.onResult) {
      this.onResult = callbacks.onResult
    }
    if (callbacks.onError) {
      this.onError = callbacks.onError
    }
    if (callbacks.onStatusChange) {
      this.onStatusChange = callbacks.onStatusChange
    }
  }
}

export default SpeechRecognitionService
